import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useParams, Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useProducts } from '../context/ProductContext';
import { Subcategory, Product, getFileUrl } from '../services/api';
import SEO from '../components/SEO/SEO';
import './DynamicServicePage.css';

const DynamicServicePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { subcategorySlug } = useParams<{ subcategorySlug: string }>();

  // Determine category type from the URL path
  const categoryType = location.pathname.startsWith('/software') ? 'software' : 'product';

  const {
    softwareCategory,
    productServiceCategory,
    loading,
    error,
    findSubcategoryBySlug
  } = useProducts();

  const [activeTab, setActiveTab] = useState<'overview' | 'key-features' | 'why-choose'>('overview');
  const [currentProductIndex, setCurrentProductIndex] = useState<number>(0);
  const [currentProductGroup, setCurrentProductGroup] = useState<number>(0);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sliderRef = useRef<HTMLDivElement | null>(null);

  // Determine which category to use based on the URL
  const apiCategoryType: 'software' | 'product and service' =
    categoryType === 'software' ? 'software' : 'product and service';

  // Find the subcategory
  const subcategory: Subcategory | undefined = subcategorySlug
    ? findSubcategoryBySlug(apiCategoryType, subcategorySlug)
    : undefined;

  // Get related subcategories (other items from same category)
  const relatedSubcategories = React.useMemo(() => {
    const category = categoryType === 'software' ? softwareCategory : productServiceCategory;
    if (!category || !subcategory) return [];
    return category.subcategories.filter(sub => sub.id !== subcategory.id).slice(0, 4);
  }, [categoryType, softwareCategory, productServiceCategory, subcategory]);

  // Slider config & derived data – kept before any early returns to satisfy Hooks rules
  // Responsive products per view: 3 on desktop, 2 on tablet, 1 on mobile
  const getProductsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 3;
      if (window.innerWidth >= 768) return 2;
      return 1;
    }
    return 3;
  };

  const [productsPerView, setProductsPerView] = useState(getProductsPerView());
  const products: Product[] = React.useMemo(() => subcategory?.products ?? [], [subcategory]);

  // Update products per view on window resize
  useEffect(() => {
    const handleResize = () => {
      setProductsPerView(getProductsPerView());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalProductGroups = products.length
    ? Math.max(1, Math.ceil(products.length / productsPerView))
    : 0;

  // Compute visible products for the full-width slider (show only 3 at a time)
  const visibleSliderProducts = React.useMemo(() => {
    if (!products.length) return [] as { product: Product; index: number }[];
    const startIndex = currentProductGroup * productsPerView;
    const items: { product: Product; index: number }[] = [];
    for (let i = 0; i < productsPerView; i++) {
      const globalIndex = startIndex + i;
      if (globalIndex >= products.length) break;
      items.push({ product: products[globalIndex], index: globalIndex });
    }
    return items;
  }, [products, currentProductGroup, productsPerView]);

  // Reset product index and group when subcategory changes
  useEffect(() => {
    setCurrentProductIndex(0);
    setCurrentProductGroup(0);
  }, [subcategorySlug]);

  // Intersection Observer for animations
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const timeoutId = setTimeout(() => {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible');
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      const sections = document.querySelectorAll('.fade-in-section');
      sections.forEach((section) => {
        observerRef.current?.observe(section);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [activeTab, subcategory]);

  // Loading state
  if (loading) {
    return (
      <div className="dynamic-service-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('dynamicServicePage.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dynamic-service-page">
        <div className="dynamic-service-page">
          <div className="error-container">
            <h2>{t('dynamicServicePage.errorTitle')}</h2>
            <p>{error}</p>
            <Link to="/" className="btn-primary">{t('dynamicServicePage.goHome')}</Link>
          </div>
        </div>
      </div>
    );
  }

  // Not found
  if (!subcategory) {
    return <Navigate to="/services" replace />;
  }

  const categoriesName = subcategory.categoriesName;
  const briefDescription = subcategory.briefDescription;

  // Helper to generate translation key from product name
  const getProductTranslationKey = (name: string, type: 'title' | 'description' | 'features' | 'benefits') => {
    if (!name) return '';
    // Simplify name to camelCase for key: "Windows Server" -> "windowsServer"
    const camelCaseName = name
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());

    // Handle standard cases manually if easier, or rely on camelCase
    // We'll try to find keys under "dynamicContent.[camelCaseName].[type]"
    return `dynamicContent.${camelCaseName}.${type}`;
  };

  // Get current product based on slider index
  const currentProduct = products.length > 0 ? products[currentProductIndex] : null;

  // Try to translate title
  const rawTitle = currentProduct?.name || categoriesName;
  const titleKey = getProductTranslationKey(rawTitle, 'title');
  const displayTitle = i18n.exists(titleKey) ? t(titleKey) : rawTitle;



  // Parse description from current product only (not subcategory description)
  // Try translation first
  const rawDescription = currentProduct?.desc
    ? currentProduct.desc
    : products.length > 0 && products[0].desc
      ? products[0].desc
      : null;

  const descriptionKey = getProductTranslationKey(currentProduct?.name || categoriesName, 'description');

  const serviceDescription = i18n.exists(descriptionKey)
    ? t(descriptionKey)
    : rawDescription || t('dynamicServicePage.overview.descriptionFallback', { name: displayTitle });

  // Get key features from current product
  const rawFeatures = currentProduct?.key_feature
    ? (typeof currentProduct.key_feature === 'string'
      ? currentProduct.key_feature.split(',').map(f => f.trim()).filter(f => f)
      : Array.isArray(currentProduct.key_feature)
        ? currentProduct.key_feature
        : [])
    : [];

  const featuresKey = getProductTranslationKey(currentProduct?.name || categoriesName, 'features');
  const currentFeatures = i18n.exists(featuresKey)
    ? t(featuresKey, { returnObjects: true }) as string[]
    : rawFeatures;

  // Get benefits/why choose from current product
  const rawBenefits = currentProduct?.why_choose_our_solution
    ? (typeof currentProduct.why_choose_our_solution === 'string'
      ? currentProduct.why_choose_our_solution.split(',').map(b => b.trim()).filter(b => b)
      : Array.isArray(currentProduct.why_choose_our_solution)
        ? currentProduct.why_choose_our_solution
        : [])
    : [];

  // Note: we might want a translation for benefits too if needed
  const currentBenefits = rawBenefits;

  // Handle product slider navigation
  const goToNextProduct = () => {
    if (!products.length || totalProductGroups <= 1) return;
    setCurrentProductGroup((prevGroup) => {
      const nextGroup = (prevGroup + 1) % totalProductGroups;
      const firstIndexOfGroup = nextGroup * productsPerView;
      setCurrentProductIndex(
        firstIndexOfGroup < products.length ? firstIndexOfGroup : products.length - 1
      );
      return nextGroup;
    });
  };

  const goToPrevProduct = () => {
    if (!products.length || totalProductGroups <= 1) return;
    setCurrentProductGroup((prevGroup) => {
      const prev = (prevGroup - 1 + totalProductGroups) % totalProductGroups;
      const firstIndexOfGroup = prev * productsPerView;
      setCurrentProductIndex(
        firstIndexOfGroup < products.length ? firstIndexOfGroup : products.length - 1
      );
      return prev;
    });
  };

  const goToProduct = (index: number) => {
    if (index >= 0 && index < products.length) {
      setCurrentProductIndex(index);
      setCurrentProductGroup(Math.floor(index / productsPerView));
    }
  };

  const generateSlugLocal = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  // Get video URL if available
  const videoUrl = subcategory.video ? getFileUrl(subcategory.video) : null;

  return (
    <div className="dynamic-service-page">
      <SEO title={categoriesName} description={`${categoriesName} - Professional solutions and products by Cool & Care in DRC.`} keywords={`${categoriesName}, Cool and Care, DRC, Kinshasa`} />
      {/* Hero Section with Video Background */}
      <section className="dsp-hero-section">
        {videoUrl ? (
          <div className="dsp-hero-video-background">
            <video
              src={videoUrl}
              autoPlay
              loop
              muted
              playsInline
              className="dsp-hero-video"
            />
            <div className="dsp-hero-video-overlay"></div>
          </div>
        ) : (
          <>
            <div className="dsp-hero-gradient"></div>
            <div className="dsp-hero-pattern"></div>
          </>
        )}
        <div className="hero-content">
          <div className="container">
            <nav className="breadcrumb-nav">
              <Link to="/" className="breadcrumb-link">{t('dynamicServicePage.hero.home')}</Link>
              <span className="breadcrumb-separator">/</span>
              <Link to="/services" className="breadcrumb-link">{t('dynamicServicePage.hero.services')}</Link>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">{(() => {
                const key = getProductTranslationKey(categoriesName, 'title');
                return i18n.exists(key) ? t(key) : categoriesName;
              })()}</span>
            </nav>
            <div className="hero-text-wrapper">
              <div className="hero-text">
                <h1 className="hero-title">{(() => {
                  const key = getProductTranslationKey(categoriesName, 'title');
                  return i18n.exists(key) ? t(key) : categoriesName;
                })()}</h1>
                <p className="hero-subtitle">{(() => {
                  const key = getProductTranslationKey(categoriesName, 'description');
                  // Use briefDescription as native fallback if no translation
                  return i18n.exists(key) ? t(key) : (briefDescription || '');
                })()}</p>
                <div className="hero-actions">
                  <Link to="/contact" className="btn-primary-hero">
                    {t('dynamicServicePage.hero.getQuote')}
                  </Link>
                  <Link to="/contact" className="btn-secondary-hero">
                    {t('dynamicServicePage.hero.scheduleConsultation')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="dsp-content-section">
        <div className="container">
          {/* Tabs */}
          <div className="dsp-tabs">
            <button
              className={`dsp-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              {t('dynamicServicePage.tabs.overview')}
            </button>
            <button
              className={`dsp-tab ${activeTab === 'key-features' ? 'active' : ''}`}
              onClick={() => setActiveTab('key-features')}
            >
              {t('dynamicServicePage.tabs.keyFeatures')}
            </button>
            <button
              className={`dsp-tab ${activeTab === 'why-choose' ? 'active' : ''}`}
              onClick={() => setActiveTab('why-choose')}
            >
              {t('dynamicServicePage.tabs.whyChoose')}
            </button>
          </div>

          {/* Tab Content */}
          <div className="dsp-tab-content">
            {activeTab === 'overview' && (
              <div className="dsp-overview-panel fade-in-section">
                <div className="dsp-overview-grid">
                  <div className="dsp-overview-main">
                    {/* Translate Name in "About {Name}" */}
                    <h2>{(() => {
                      const rawName = currentProduct?.name || categoriesName;
                      const key = getProductTranslationKey(rawName, 'title');
                      const translatedName = i18n.exists(key) ? t(key) : rawName;
                      return t('dynamicServicePage.overview.about', { name: translatedName });
                    })()}</h2>
                    <p>{serviceDescription}</p>
                    <p>{t('dynamicServicePage.overview.qualityAssurance')}</p>
                  </div>

                  {/* Sidebar with Product Image */}
                  <div className="dsp-overview-sidebar">
                    {/* Current Product Image */}
                    {currentProduct && currentProduct.photo && (
                      <div className="current-product-image-container">
                        <img
                          src={getFileUrl(currentProduct.photo) || currentProduct.photo}
                          alt={(() => {
                            const key = getProductTranslationKey(currentProduct.name, 'title');
                            return i18n.exists(key) ? t(key) : currentProduct.name;
                          })()}
                          className="current-product-image"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'key-features' && (
              <div className="dsp-overview-panel fade-in-section">
                <div className="dsp-overview-grid dsp-full-width-grid">
                  <div className="dsp-overview-main dsp-full-width-main">
                    {currentFeatures.length > 0 ? (
                      <div className="dsp-features-grid">
                        {currentFeatures.map((feature, index) => (
                          <div key={index} className="dsp-feature-card">
                            <div className="dsp-feature-number">{index + 1}</div>
                            <p>{feature}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>{t('dynamicServicePage.overview.noFeatures')}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'why-choose' && (
              <div className="dsp-overview-panel fade-in-section">
                <div className="dsp-overview-grid dsp-full-width-grid">
                  <div className="dsp-overview-main dsp-full-width-main">
                    {currentBenefits.length > 0 ? (
                      <div className="dsp-benefits-grid">
                        {currentBenefits.map((benefit, index) => (
                          <div key={index} className="dsp-benefit-card">
                            <span className="dsp-benefit-check">✓</span>
                            <p>{benefit}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>{t('dynamicServicePage.overview.noBenefits')}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Full Width Product Slider - Show if there are products */}
      {products.length > 0 && (
        <section className="full-width-product-slider-section">
          <div className="container">
            <h3 className="slider-section-title">
              {categoryType === 'software' ? t('dynamicServicePage.slider.browseSolutions') : t('dynamicServicePage.slider.browseProducts')}
            </h3>
            <div className="full-width-slider-wrapper">
              {totalProductGroups > 1 && (
                <button
                  className="full-slider-nav-btn full-slider-prev"
                  onClick={goToPrevProduct}
                  aria-label={t('common.previousProduct')}
                  disabled={totalProductGroups <= 1}
                >
                  ‹
                </button>
              )}
              <div className="full-width-product-slider" ref={sliderRef}>
                {visibleSliderProducts.map(({ product, index }) => {
                  // Calculate translations for slider items
                  const productTitleKey = getProductTranslationKey(product.name, 'title');
                  const productTitle = i18n.exists(productTitleKey) ? t(productTitleKey) : (product.name || 'Product');

                  const productDescKey = getProductTranslationKey(product.name, 'description');
                  const productDesc = i18n.exists(productDescKey) ? t(productDescKey) : product.desc;

                  return (
                    <div
                      key={product.id || index}
                      className={`full-width-slide ${index === currentProductIndex ? 'active' : ''}`}
                      onClick={() => goToProduct(index)}
                    >
                      {product.photo ? (
                        <div className="full-slide-image">
                          <img
                            src={getFileUrl(product.photo) || product.photo}
                            alt={productTitle}
                            loading="lazy"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent && !parent.querySelector('.full-slide-placeholder')) {
                                parent.innerHTML = `
                                <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor" style="color: #9ca3af; opacity: 0.6;">
                                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                                </svg>
                              `;
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div className="full-slide-image full-slide-placeholder">
                          <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                          </svg>
                        </div>
                      )}
                      <div className="full-slide-info">
                        <h4 className="full-slide-name">
                          {productTitle}
                        </h4>
                        {productDesc && (
                          <p className="full-slide-desc">
                            {productDesc.length > 150 ? `${productDesc.substring(0, 150)}...` : productDesc}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalProductGroups > 1 && (
                <button
                  className="full-slider-nav-btn full-slider-next"
                  onClick={goToNextProduct}
                  aria-label={t('common.nextProduct')}
                  disabled={totalProductGroups <= 1}
                >
                  ›
                </button>
              )}
            </div>
            {/* Slider Dots - based on groups of 3 products */}
            {totalProductGroups > 1 && (
              <div className="full-slider-dots">
                {Array.from({ length: totalProductGroups }).map((_, groupIndex) => (
                  <button
                    key={groupIndex}
                    className={`full-slider-dot ${groupIndex === currentProductGroup ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentProductGroup(groupIndex);
                      const firstIndexOfGroup = groupIndex * productsPerView;
                      setCurrentProductIndex(
                        firstIndexOfGroup < products.length ? firstIndexOfGroup : products.length - 1
                      );
                    }}
                    aria-label={`Go to product group ${groupIndex + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Related Services Section */}
      {relatedSubcategories.length > 0 && (
        <section className="dsp-related-section fade-in-section">
          <div className="container">
            <h2 className="dsp-related-title">{categoryType === 'software' ? t('dynamicServicePage.related.softwareTitle') : t('dynamicServicePage.related.productsTitle')}</h2>
            <div className="dsp-related-grid">
              {relatedSubcategories.map((related) => {
                // Translate related category title
                const relatedKey = getProductTranslationKey(related.categoriesName, 'title');
                const relatedName = i18n.exists(relatedKey) ? t(relatedKey) : related.categoriesName;

                return (
                  <Link
                    key={related.id}
                    to={`/${categoryType}/${generateSlugLocal(related.categoriesName)}`}
                    className="dsp-related-card"
                  >
                    <div className="dsp-related-content">
                      <h3>{relatedName}</h3>
                      <span className="dsp-related-count">{t('dynamicServicePage.related.itemsCount', { count: related.products.length })}</span>
                      <span className="dsp-related-arrow">→</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}


      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="dsp-modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="dsp-modal" onClick={e => e.stopPropagation()}>
            <button className="dsp-modal-close" onClick={() => setSelectedProduct(null)}>×</button>

            {selectedProduct.photo && (
              <div className="dsp-modal-image">
                <img
                  src={getFileUrl(selectedProduct.photo) || selectedProduct.photo}
                  alt={selectedProduct.name}
                />
              </div>
            )}

            <div className="dsp-modal-content">
              {/* Calculate translated name */}
              {(() => {
                const titleKey = getProductTranslationKey(selectedProduct.name, 'title');
                const displayTitle = i18n.exists(titleKey) ? t(titleKey) : selectedProduct.name;

                return (
                  <>
                    <h2>{displayTitle}</h2>

                    {selectedProduct.desc && (
                      <div className="dsp-modal-section">
                        <h4>{t('dynamicServicePage.tabs.overview')}</h4>
                        <p>{(() => {
                          const descKey = getProductTranslationKey(selectedProduct.name, 'description');
                          return i18n.exists(descKey) ? t(descKey) : selectedProduct.desc;
                        })()}</p>
                      </div>
                    )}

                    {selectedProduct.key_feature && (
                      <div className="dsp-modal-section">
                        <h4>{t('dynamicServicePage.tabs.keyFeatures')}</h4>
                        <p>{selectedProduct.key_feature}</p>
                      </div>
                    )}

                    {selectedProduct.why_choose_our_solution && (
                      <div className="dsp-modal-section">
                        <h4>{t('dynamicServicePage.modal.whyChoose')}</h4>
                        <p>{selectedProduct.why_choose_our_solution}</p>
                      </div>
                    )}

                    <Link to="/contact" className="dsp-modal-btn">{t('dynamicServicePage.modal.getQuote', { name: displayTitle })}</Link>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

export default DynamicServicePage;

